import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PageBreadcrumb from '../components/PageBreadcrumb';
import { EyeOffIcon, EyeOnIcon, LockIcon } from '../components/Icons';
import { useEmoji } from '../context/EmojiContext';
import { changePassword, fetchCurrentUser } from '../services/authService';
import '../components/DataPage.css';
import './Profile.css';

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  showPassword,
  onToggleShow,
  disabled,
  autoComplete,
  showLockIcon = true,
  placeholder,
}) {
  return (
    <label className="profile__field data-page__modal-field" htmlFor={id}>
      <span>{label}</span>
      <div
        className={`profile__field-control${showLockIcon ? '' : ' profile__field-control--no-icon'}`}
      >
        {showLockIcon && (
          <span className="profile__field-icon" aria-hidden="true">
            <LockIcon />
          </span>
        )}
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          autoComplete={autoComplete}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="profile__toggle-password"
          onClick={onToggleShow}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          disabled={disabled}
        >
          {showPassword ? <EyeOnIcon /> : <EyeOffIcon />}
        </button>
      </div>
    </label>
  );
}

function Profile() {
  const { isEmojified, emojify } = useEmoji();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [lastPasswordChange, setLastPasswordChange] = useState(null);
  const [accountCreated, setAccountCreated] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadUserDetails() {
      try {
        const user = await fetchCurrentUser();
        if (!cancelled) {
          setUserEmail(user?.email ?? '');
          setAccountCreated(user?.createdAt ?? null);
          setLastPasswordChange(user?.updatedAt ?? user?.createdAt ?? null);
        }
      } catch {
        if (!cancelled) {
          setUserEmail('');
          setAccountCreated(null);
          setLastPasswordChange(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingDetails(false);
        }
      }
    }

    loadUserDetails();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required.');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      toast.error('New password must be different from your current password.');
      return;
    }

    setSubmitting(true);

    try {
      const updatedUser = await changePassword({ currentPassword, newPassword });
      setLastPasswordChange(updatedUser?.updatedAt ?? new Date().toISOString());
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message || 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="profile">
      <PageBreadcrumb current={emojify('👤', 'Profile')} />

      <div className="profile__header">
        <h1>{emojify('👤', 'Profile')}</h1>
        <p>View your account and change your password.</p>
      </div>

      <form className="profile__card" onSubmit={handleSubmit} noValidate>
        <div className="profile__layout">
          <div className="profile__details-column">
            <aside className="profile__details-box" aria-label="Account details">
              <h2>{emojify('👤', 'Account')}</h2>
              <dl className="profile__details-list">
                <div className="profile__details-item">
                  <dt>Email</dt>
                  <dd>{loadingDetails ? 'Loading...' : userEmail || 'Not available'}</dd>
                </div>
              </dl>
            </aside>

            <aside className="profile__details-box" aria-label="Account created">
              <h2>Created</h2>
              <dl className="profile__details-list">
                <div className="profile__details-item">
                  <dt>Created on</dt>
                  <dd>{loadingDetails ? 'Loading...' : formatDateTime(accountCreated)}</dd>
                </div>
              </dl>
            </aside>

            <aside className="profile__details-box" aria-label="Last password change">
              <h2>Password last changed</h2>
              <dl className="profile__details-list">
                <div className="profile__details-item">
                  <dt>Last changed</dt>
                  <dd>{loadingDetails ? 'Loading...' : formatDateTime(lastPasswordChange)}</dd>
                </div>
              </dl>
            </aside>
          </div>

          <div className="profile__fields">
            <h2 className="profile__security-heading">{emojify('🔒', 'Security')}</h2>

            <PasswordField
              id="profile-current-password"
              label={emojify('🔒', 'Current Password')}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              showPassword={showCurrentPassword}
              onToggleShow={() => setShowCurrentPassword((current) => !current)}
              disabled={submitting}
              autoComplete="current-password"
              showLockIcon={!isEmojified}
              placeholder="Enter current password"
            />

            <PasswordField
              id="profile-new-password"
              label={emojify('🔒', 'New Password')}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              showPassword={showNewPassword}
              onToggleShow={() => setShowNewPassword((current) => !current)}
              disabled={submitting}
              autoComplete="new-password"
              showLockIcon={!isEmojified}
              placeholder="Enter new password"
            />

            <PasswordField
              id="profile-confirm-password"
              label={emojify('🔒', 'Confirm New Password')}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              showPassword={showConfirmPassword}
              onToggleShow={() => setShowConfirmPassword((current) => !current)}
              disabled={submitting}
              autoComplete="new-password"
              showLockIcon={!isEmojified}
              placeholder="Confirm new password"
            />

            <button type="submit" className="profile__submit" disabled={submitting}>
              {submitting ? emojify('💾', 'Saving...') : emojify('💾', 'Save')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Profile;
